import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from 'react';
import { useSettings } from '../hooks/useSettings';
import { capture } from '../lib/posthog';
import { shouldShowOnboarding as _shouldShowOnboarding, KEYS } from '../lib/onboardingState';

/** Lighten/darken a hex color by % (basic) for hover/light variants */
function shadeHex(hex, percent) {
  const h = hex.replace('#', '');
  const num = parseInt(h, 16);
  const r = Math.max(0, Math.min(255, ((num >> 16) & 0xff) + percent));
  const g = Math.max(0, Math.min(255, ((num >> 8) & 0xff) + percent));
  const b = Math.max(0, Math.min(255, (num & 0xff) + percent));
  return '#' + [r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('');
}

/** Resolve theme: 'auto' -> 'light' or 'dark' based on prefers-color-scheme */
function resolveTheme(theme) {
  if (theme === 'light' || theme === 'dark') return theme;
  if (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-color-scheme: light)').matches
  ) {
    return 'light';
  }
  return 'dark';
}

const AI_DISCLOSURE_KEY = 'melodiza:ai-disclosure-acknowledged-v1';

const SettingsContext = createContext(null);

export function SettingsProvider({ children }) {
  const { settings, update, reset } = useSettings();

  const [showOnboarding, setShowOnboarding] = useState(() =>
    _shouldShowOnboarding()
  );
  const [userPrefs, setUserPrefs] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(KEYS.ONBOARDED) || 'null');
    } catch {
      return null;
    }
  });

  const [hasAcknowledgedAIDisclosure, setHasAcknowledgedAIDisclosure] =
    useState(() => {
      return localStorage.getItem(AI_DISCLOSURE_KEY) === 'true';
    });

  const acknowledgeAIDisclosure = () => {
    localStorage.setItem(AI_DISCLOSURE_KEY, 'true');
    setHasAcknowledgedAIDisclosure(true);
    // TODO C.5+: appeler trpc.compliance.acknowledgeAIDisclosure quand trpc client disponible côté JS avec user authentifié
  };

  const relaunchAIDisclosure = () => {
    localStorage.removeItem(AI_DISCLOSURE_KEY);
    setHasAcknowledgedAIDisclosure(false);
  };

  // Applique le theme (light/dark/auto) au DOM root
  useEffect(() => {
    const resolved = resolveTheme(settings.theme);
    document.documentElement.setAttribute('data-theme', resolved);
  }, [settings.theme]);

  // Applique l'accent color aux variables CSS root
  useEffect(() => {
    const root = document.documentElement.style;
    const accent = settings.accentColor || '#e8a87c';
    root.setProperty('--accent', accent);
    root.setProperty('--accent-hover', shadeHex(accent, -30));
    root.setProperty('--accent-light', shadeHex(accent, 30));
  }, [settings.accentColor]);

  // Wrap update to capture business_changed PostHog event
  const updateSettingsWithTracking = useCallback(
    (patch) => {
      if (
        patch.business?.type &&
        patch.business.type !== settings.business?.type
      ) {
        capture('business_changed', {
          from: settings.business?.type,
          to: patch.business.type,
        });
      }
      update(patch);
    },
    [update, settings.business?.type]
  );

  const value = useMemo(
    () => ({
      settings,
      updateSettings: updateSettingsWithTracking,
      resetSettings: reset,
      showOnboarding,
      setShowOnboarding,
      userPrefs,
      setUserPrefs,
      hasAcknowledgedAIDisclosure,
      acknowledgeAIDisclosure,
      relaunchAIDisclosure,
    }),
    // eslint-disable-next-line -- react-hooks/exhaustive-deps: intentional partial deps
    [
      settings,
      updateSettingsWithTracking,
      reset,
      showOnboarding,
      userPrefs,
      hasAcknowledgedAIDisclosure,
    ]
  );

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettingsContext() {
  const ctx = useContext(SettingsContext);
  if (!ctx)
    throw new Error('useSettingsContext must be inside SettingsProvider');
  return ctx;
}
